import { aximCoreClient } from "../../lib/supabaseClient.js";
import React, { useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiDownloadCloud, FiFileText } from 'react-icons/fi';
import { useDesktopAgentStore } from '../../store/useDesktopAgentStore';

export default function DropZone({ targetApplication }) {
  const [isDragging, setIsActiveDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversionStatus, setConversionStatus] = useState('');
  const [currentPayloadSize, setCurrentPayloadSize] = useState(0);
  const [detectedFormat, setDetectedFormat] = useState('RAW');
  const [isSuccess, setIsSuccess] = useState(false);
  const { operatorAddress, localQueueCount, incrementLocalBufferQueue, addActionLog, updateCloudflareMetrics, systemStatus, setSystemStatus, isLiveChannelConnected } = useDesktopAgentStore();

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsActiveDragging(false);
    updateCloudflareMetrics();

    let droppedText = '';
    let droppedFile = null;

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === 'string' && e.dataTransfer.items[i].type.match('^text/plain')) {
          droppedText = await new Promise(resolve => e.dataTransfer.items[i].getAsString(resolve));
          break;
        } else if (e.dataTransfer.items[i].kind === 'file') {
          droppedFile = e.dataTransfer.items[i].getAsFile();
          droppedText = await droppedFile.text();
          break;
        }
      }
    } else {
      for (let i = 0; i < e.dataTransfer.files.length; i++) {
        droppedFile = e.dataTransfer.files[i];
        droppedText = await droppedFile.text();
        break;
      }
    }


    let format = 'RAW';
    const trimmedText = droppedText.trim();
    if (trimmedText.startsWith('{') || trimmedText.startsWith('[')) {
      format = 'JSON';
    } else if (trimmedText.includes(',') && (trimmedText.includes('\n') || trimmedText.includes('\r\n'))) {
      format = 'CSV';
    }
    setDetectedFormat(format);

    const payloadSizeKb = (new Blob([droppedText]).size / 1024).toFixed(1);
    setCurrentPayloadSize(payloadSizeKb);

    // Asguard Interceptor Shield
    const lowerText = droppedText.toLowerCase();
    const forbiddenStrings = ['rm -rf', 'sudo', 'drop table', 'format c:'];
    const matchedSignature = forbiddenStrings.find(str => lowerText.includes(str));

    if (matchedSignature) {
      addActionLog({ type: 'error', text: `[FAULT] [ASGUARD_SHIELD] Intercepted malicious payload signature: "${matchedSignature}". Connection severed.` });
      setSystemStatus('ERROR');
      return;
    }

    setIsProcessing(true);

    let processedFile = droppedFile;
    let processedText = droppedText;
    const filename = droppedFile ? droppedFile.name : `upload_${Date.now()}.${format.toLowerCase()}`;
    const needsConversion = /\.(pdf|docx|html|png|jpeg|jpg)$/i.test(filename);

    if (needsConversion && droppedFile) {
    addActionLog({ type: 'system', text: `[INGRESS] Detected complex document format (${detectedFormat}). Routing to Cloudflare Workers AI for semantic Markdown extraction...` });
      setConversionStatus(`[WORKERS_AI] Converting [${filename}] to semantic Markdown...`);
      try {
        const conversionFormData = new FormData();
        conversionFormData.append('file', droppedFile);

        const response = await fetch('/v1/ai/tomarkdown', {
          method: 'POST',
          body: conversionFormData
        });
        if (response.ok) {
          processedText = await response.text();
          processedFile = new File([processedText], `${filename}.md`, { type: 'text/markdown' });
          format = 'MARKDOWN';
        }
      } catch (err) {
        console.warn('Edge conversion failed', err);
      }
      setConversionStatus('');
    } else {
      await new Promise(resolve => setTimeout(resolve, 800));
    }

    try {
      const formData = new FormData();
      if (processedFile) {
        formData.append('document', processedFile);
      } else {
        const blob = new Blob([processedText], { type: 'text/plain' });
        formData.append('document', blob, `upload_${Date.now()}.${format.toLowerCase()}`);
      }
      formData.append('target_app', targetApplication);
      formData.append('session_affinity', `ses_${operatorAddress || 'lax_primary'}`);

      const { data, error } = await aximCoreClient.functions.invoke('knowledge-ingest', {
        body: formData,
      });

      if (error) throw error;

      const filename = droppedFile ? droppedFile.name : `upload_${Date.now()}.${format.toLowerCase()}`;
      addActionLog({ type: 'success', text: `[INGRESS] Document [${filename}] successfully processed and vectorized into central RAG Memory Banks.` });
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 2500);
    } catch (error) {
      console.warn('[BROWSER_SIMULATION] Ingestion captured into virtual local loop', error);
      addActionLog({ type: 'fault', text: `[FAULT] Edge ingestion failed. Captured into virtual local loop.` });
      incrementLocalBufferQueue();
      addActionLog({ type: 'task', text: `Batch ingestion staged into virtual queue (depth: ${localQueueCount + 1}): [${targetApplication}] // PAYLOAD_SIZE: ${payloadSizeKb} KB` });

      if (targetApplication === 'green_machine') {
        addActionLog({ type: 'system', text: '[ENRICHMENT] Semantic parse routed affiliate/ledger records to Green Machine financial registry' });
      } else if (targetApplication === 'nexus_crm') {
        addActionLog({ type: 'system', text: '[ENRICHMENT] Semantic parse routed B2B contact data blocks to Nexus CRM sheets' });
      } else if (targetApplication === 'asguard_soc') {
        addActionLog({ type: 'system', text: '[ENRICHMENT] Semantic parse routed incident anomaly trace logs to Asguard WAF threat feed maps' });
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <motion.div
      onDragOver={(e) => { e.preventDefault(); if (systemStatus !== 'ERROR') setIsActiveDragging(true); }}
      onDragLeave={() => setIsActiveDragging(false)}
      onDrop={systemStatus === 'ERROR' ? (e) => { e.preventDefault(); setIsActiveDragging(false); } : handleDrop}
      animate={{ 
        borderColor: systemStatus === 'ERROR' ? '#ef4444' : (isDragging ? '#10b981' : '#1e293b'),
        backgroundColor: systemStatus === 'ERROR' ? 'rgba(127, 29, 29, 0.2)' : (isDragging ? 'rgba(16,185,129,0.05)' : 'transparent')
      }}
      className={`relative flex-1 min-h-[160px] border border-dashed rounded-xl flex flex-col items-center justify-center p-6 transition-all ${systemStatus === 'ERROR' ? 'cursor-not-allowed bg-red-950/20' : 'cursor-crosshair'} group`}
    >

      {isSuccess && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-emerald-950/40 rounded-xl backdrop-blur-sm z-10 animate-pulse">
          <span className="text-emerald-400 text-lg mb-2">✔</span>
          <span className="text-[10px] font-mono font-bold text-emerald-400 tracking-widest">[VECTOR_INGESTION_SUCCESS]</span>
        </div>
      )}
      <div className="min-h-[64px] flex flex-col justify-center items-center gap-3 pointer-events-none">
        {isProcessing ? (
          <>
            <div className="w-10 h-10 rounded-full border-2 border-emerald-500/20 border-t-emerald-500 animate-spin shadow-[0_0_15px_rgba(16,185,129,0.3)]"></div>
            <div className="text-center">
              <p className="text-[10px] text-emerald-400 font-mono font-bold tracking-widest animate-pulse">
                {conversionStatus || `PARSING_EDGE_INGRESS_CHUNKS [${detectedFormat}] (${currentPayloadSize} KB)...`}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center group-hover:border-emerald-500/50 transition-colors">
              <SafeIcon icon={systemStatus === 'ERROR' ? null : FiFileText} className={`transition-colors ${systemStatus === 'ERROR' ? 'text-red-500 animate-pulse' : 'text-slate-600 group-hover:text-emerald-500'}`} />
            </div>
            <div className="text-center">
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{systemStatus === 'ERROR' ? 'NODE WORKLOAD SUSPENDED' : 'Drop Command Frame'}</p>
              <p className={`text-[8px] mt-1 font-bold tracking-wider ${isLiveChannelConnected ? 'text-slate-600' : 'text-amber-500/80'}`}>
       {systemStatus === 'ERROR'
         ? 'Clear local out-of-band cache blocks to restore ingestion channels'
         : isLiveChannelConnected
           ? 'JSON / CSV / RAW'
           : '[OFFLINE] Payloads will queue to virtual local buffer'}
     </p>
              {systemStatus === 'ERROR' && (
                <span className="text-[9px] text-amber-500 font-bold tracking-widest uppercase animate-pulse mt-2 block">
                  [GATEWAY_ALERT] Data stream propagation frozen by Asguard Security Shield.
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}
